/**
 * AttributionBadge.js
 *
 * Displays who created/modified an entry and when.
 * Part of FT-06: User Attribution on Entries
 *
 * Usage:
 *   <AttributionBadge createdBy="John Smith" createdAt="2026-03-22T10:30:00Z" />
 *   <AttributionBadge createdBy={{ name: "John Smith", role: "Master" }} createdAt={1711099800000} />
 */

import React from 'react';

/**
 * Format date/time for display: "DD Mon YYYY HH:MM"
 */
const formatDateTime = (dateValue) => {
  if (!dateValue) return null;

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;

    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    return `${day} ${month} ${year} ${time}`;
  } catch {
    return null;
  }
};

/**
 * Get display name from createdBy (handles string or object)
 * FT-06b: Exported for use in search/filter operations
 */
export const getCreatedByName = (createdBy) => {
  if (!createdBy) return null;

  // String format (current)
  if (typeof createdBy === 'string') {
    return createdBy;
  }

  // Object format (future FT-06b: { userId, name, role })
  if (typeof createdBy === 'object') {
    const name = createdBy.name || createdBy.userName || 'Unknown';
    const role = createdBy.role;
    return role ? `${name} (${role})` : name;
  }

  return null;
};

/**
 * AttributionBadge - displays entry attribution
 *
 * @param {string|object} createdBy - Creator name (string) or object { name, role }
 * @param {string|number} createdAt - ISO date string or epoch timestamp
 * @param {string} className - Optional additional classes
 */
const AttributionBadge = ({ createdBy, createdAt, className = '' }) => {
  const name = getCreatedByName(createdBy);
  const dateTime = formatDateTime(createdAt);

  // If no attribution data, render nothing
  if (!name && !dateTime) return null;

  // Build display string
  let displayText = '';
  if (name && dateTime) {
    displayText = `${name} — ${dateTime}`;
  } else if (name) {
    displayText = name;
  } else if (dateTime) {
    displayText = dateTime;
  }

  return (
    <span className={`text-xs text-gray-400 ${className}`}>
      {displayText}
    </span>
  );
};

export default AttributionBadge;
